import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Button,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import ErrorIcon from "@mui/icons-material/Error";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./styles.css";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { dashboardApi } from "../providers/env";
import { Params, useParams } from "react-router";
const StatCard = ({
  icon,
  title,
  value,
  suffix,
  className,
}: {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  suffix?: string;
  className?: string;
}) => (
  <Card className={className}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 80,
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgb(209, 243, 255)",
            borderRadius: 2,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4">
            {value} {suffix}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Chart = ({
  chartData,
}: {
  chartData: { name: string; good: number; bad: number }[];
}) => {
  const totals = chartData?.reduce(
    (acc, cur) => ({
      good: acc.good + cur.good,
      bad: acc.bad + cur.bad,
    }),
    { good: 0, bad: 0 }
  );
  const total = totals?.good + totals?.bad;

  const getPercent = (value: number) => {
    if (total === 0 || Number.isNaN(total)) {
      return "0%";
    }
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          borderRadius: 1,
          padding: 1.5,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" mb={0.5}>
          {label}
        </Typography>

        {payload.map((entry: any) => (
          <Box key={entry.dataKey} display="flex" gap={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                borderRadius: 0.5,
              }}
            />
            <Typography variant="body2">
              <strong>{entry.value}</strong> {entry.name}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" justifyContent="flex-start" gap={3} mb={3}>
          <Box display="flex" alignItems="start" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: "#4caf50",
                borderRadius: 0.5,
                transform: "translateY(20%)",
              }}
            />
            <Box>
              <Typography variant="h6" color="text.secondary">
                Rated good
              </Typography>
              <Typography variant="h6" color="#000" fontWeight={600}>
                {totals?.good || 0} ({getPercent(totals?.good || 0)})
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="start" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: "#f44336",
                borderRadius: 0.5,
                transform: "translateY(20%)",
              }}
            />
            <Box>
              <Typography variant="h6" color="text.secondary">
                Rated bad
              </Typography>
              <Typography variant="h6" color="#000" fontWeight={600}>
                {totals?.bad || 0} ({getPercent(totals?.bad || 0)})
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box mt={2} height={250}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />

              <Bar dataKey="good" fill="#4caf50" />
              <Bar dataKey="bad" fill="#f44336" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const formatChartData = (records: Record<string, any>) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
  });

  return Object.entries(records).map(([date, values]) => {
    const formattedDate = formatter.format(new Date(date)); // "28 Jun"
    return {
      name: formattedDate,
      chats: values.chats || 0,
      good: values.good || 0,
      bad: values.bad || 0,
    };
  });
};

const Component = () => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  const [data, setData] = useState<any>();
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const fetchRatingsReport = async () => {
    try {
      let adjustedFromDate = dayjs(fromDate).startOf("day").toDate();
      let adjustedToDate = dayjs(toDate).endOf("day").toDate();

      if (adjustedFromDate.getTime() >= adjustedToDate.getTime()) {
        adjustedFromDate = dayjs(adjustedToDate)
          .startOf("day")
          .subtract(1, "day")
          .toDate();
      }

      const from = dayjs(adjustedFromDate).format("YYYY-MM-DDTHH:mm:ssZ");
      const to = dayjs(adjustedToDate).format("YYYY-MM-DDTHH:mm:ssZ");
      const distribution = "day";

      const response = await dashboardApi.reportsChatsRatings({
        fromDate: from,
        toDate: to,
        distribution: distribution,
        agentId,
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! ${response.status}`);
      }

      const responseData = response.data;

      // format dữ liệu
      const formatted = formatChartData(responseData?.records || []);
      setData(formatted);
    } catch (error) {
      console.error("Fetch failed:", error);
    }
  };

  useEffect(() => {
    fetchRatingsReport();
  }, []);

  const totalStats = data?.reduce(
    (acc: any, cur: any) => ({
      chats: acc.chats + cur.chats,
      good: acc.good + cur.good,
      bad: acc.bad + cur.bad,
    }),
    { chats: 0, good: 0, bad: 0 }
  );

  return (
    <Box sx={{ padding: 3 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <DatePicker
            selected={fromDate}
            onChange={(date) => {
              if (date) setFromDate(date);
            }}
            dateFormat="yyyy-MM-dd"
            className="MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputAdornedEnd"
            placeholderText="Start date"
          />
        </Grid>
        <Grid item>
          <DatePicker
            selected={toDate}
            onChange={(date) => {
              if (date) setToDate(date);
            }}
            dateFormat="yyyy-MM-dd"
            className="MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputAdornedEnd"
            placeholderText="End date"
          />
        </Grid>
        <Grid item>
          <Button
            fullWidth
            variant="contained"
            sx={{ height: "100%" }}
            onClick={fetchRatingsReport}
          >
            Filter
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <StatCard
            icon={<ChatIcon sx={{ width: 50, height: 50 }} />}
            title="Good Chats"
            value={totalStats?.good || 0}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <StatCard
            icon={<ErrorIcon sx={{ width: 50, height: 50 }} />}
            title="Bad Chats"
            value={totalStats?.bad || 0}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Chart chartData={data} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Component;
